/**
 * useRuntimeData — Surface Layer (/01)
 *
 * Read hooks over the backend-owned runtime endpoints that drive the
 * route-based product views: status, notifications, projects, profile
 * (`/api/me`), and conversations. All go through the authenticated BFF
 * client (Bearer JWT); when signed-out they resolve to a typed
 * `unauthorized` error and the hooks return empty/null so views render a
 * sign-in/empty state rather than throwing.
 *
 * `tenantContextMissing` surfaces the BFF 403 `tenant_context_missing`
 * state (workspace not provisioned) distinctly from a generic error.
 */

import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { BridgeResponse } from '@contracts/idl-bridge.contract';
import type {
  AckResponse,
  ArchiveResponse,
  ClientVisibilityFilter,
  CreateClientGlobalNotificationRequest,
  CreateClientGlobalNotificationResponse,
  ConversationsResponse,
  ConversationRow,
  DocumentRow,
  DocumentsResponse,
  MeResponse,
  NotificationActResponse,
  NotificationRow,
  NotificationsResponse,
  OnboardingDraftResponse,
  ProjectRow,
  ProjectsResponse,
  ReportRow,
  ReportsResponse,
  StatusResponse,
} from '@contracts/runtime-endpoints.contract';
import { bffGet, bffPatch, bffPost, SURFACE_BFF_PATHS } from '@lib/idl-bridge/bff-read-client';
import { useAuthStore } from '@auth/auth-store';

const STATUS_PATH = '/api/status';
const NOTIFICATIONS_PATH = '/api/notifications';
const PROJECTS_PATH = '/api/projects';
const ME_PATH = '/api/me';
const CONVERSATIONS_PATH = '/api/conversations';
const DOCUMENTS_PATH = SURFACE_BFF_PATHS.documents;
const REPORTS_PATH = SURFACE_BFF_PATHS.reports;
const ONBOARDING_DRAFT_PATH = SURFACE_BFF_PATHS.onboardingDraft;

function isTenantMissing<T>(res: BridgeResponse<T> | undefined): boolean {
  return Boolean(res && !res.ok && res.error.code === 'forbidden');
}

export interface UseStatusResult {
  status: StatusResponse | null;
  tenantContextMissing: boolean;
  isLoading: boolean;
}

export function useStatus(): UseStatusResult {
  const authKey = useAuthStore((s) => s.identity?.id ?? null);
  const query = useQuery({
    queryKey: ['status', authKey],
    staleTime: 15_000,
    retry: (n) => n < 1,
    queryFn: () => bffGet<StatusResponse>(STATUS_PATH),
  });
  return useMemo(() => {
    const res = query.data;
    return {
      status: res && res.ok ? res.data : null,
      tenantContextMissing: isTenantMissing(res),
      isLoading: query.isLoading,
    };
  }, [query.data, query.isLoading]);
}

export interface NotificationActOutcome {
  ok: boolean;
  dispatchedIntent?: string | null;
  frameId?: string | null;
  /** Decision model (beta.20). */
  status?: string;
  delegatedToRole?: string | null;
  global?: boolean;
  replayed?: boolean;
}

export type NotificationsView = 'inbox' | 'unread' | 'archived';

export interface UseNotificationsResult {
  notifications: NotificationRow[];
  unreadCount: number;
  openActionable: number;
  delegatedCount: number;
  tenantContextMissing: boolean;
  isLoading: boolean;
  ack: (id: string) => Promise<boolean>;
  /** Agentic act: approve/reject/execute/delegate. Dispatches an ActionFrame server-side when permitted. */
  act: (id: string, decision: string, note?: string) => Promise<NotificationActOutcome>;
  /** Escalate above the current user's authority (delegate-up). */
  delegate: (id: string, note?: string) => Promise<NotificationActOutcome>;
  /** Email-inbox archive / unarchive. */
  archive: (id: string, archived?: boolean) => Promise<boolean>;
  /**
   * Author a client_global notification (v2, REQ-001.65). Returns
   * `status='migration_apply_pending'` until the v2 migration is applied — the
   * caller MUST NOT present that as persisted.
   */
  createClientGlobalNotification: (
    input: CreateClientGlobalNotificationRequest
  ) => Promise<CreateClientGlobalNotificationResponse>;
}

export function useNotifications(
  statusFilter?: 'unread',
  limit = 50,
  view?: NotificationsView,
  clientVisibility?: ClientVisibilityFilter
): UseNotificationsResult {
  const authKey = useAuthStore((s) => s.identity?.id ?? null);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['notifications', statusFilter ?? 'all', view ?? 'default', clientVisibility ?? 'all', limit, authKey],
    staleTime: 15_000,
    retry: (n) => n < 1,
    queryFn: () =>
      bffGet<NotificationsResponse>(NOTIFICATIONS_PATH, {
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(view ? { view } : {}),
        ...(clientVisibility && clientVisibility !== 'all' ? { client_visibility: clientVisibility } : {}),
        limit,
      }),
  });

  const ack = useCallback(
    async (id: string): Promise<boolean> => {
      const res = await bffPost<AckResponse>(`${NOTIFICATIONS_PATH}/${id}/ack`);
      if (res.ok) {
        await queryClient.invalidateQueries({ queryKey: ['notifications'] });
        return true;
      }
      return false;
    },
    [queryClient]
  );

  const act = useCallback(
    async (id: string, decision: string, note?: string): Promise<NotificationActOutcome> => {
      const res = await bffPost<NotificationActResponse>(`${NOTIFICATIONS_PATH}/${id}/act`, { decision, note });
      if (res.ok) {
        await queryClient.invalidateQueries({ queryKey: ['notifications'] });
        const r = res.data.result;
        return {
          ok: true,
          dispatchedIntent: r?.dispatched_intent ?? null,
          frameId: r?.frame_id ?? null,
          status: r?.status,
          delegatedToRole: r?.delegated_to_role ?? null,
          global: r?.global,
          replayed: r?.replayed,
        };
      }
      return { ok: false };
    },
    [queryClient]
  );

  const delegate = useCallback(
    (id: string, note?: string) => act(id, 'delegate', note),
    [act]
  );

  const archive = useCallback(
    async (id: string, archived = true): Promise<boolean> => {
      const res = await bffPost<ArchiveResponse>(`${NOTIFICATIONS_PATH}/${id}/archive`, { archived });
      if (res.ok) {
        await queryClient.invalidateQueries({ queryKey: ['notifications'] });
        return true;
      }
      return false;
    },
    [queryClient]
  );

  const createClientGlobalNotification = useCallback(
    async (input: CreateClientGlobalNotificationRequest): Promise<CreateClientGlobalNotificationResponse> => {
      const res = await bffPost<CreateClientGlobalNotificationResponse>(`${NOTIFICATIONS_PATH}/client-global`, input);
      if (res.ok) {
        // The BFF returns HTTP 200 with {ok:false, status:'migration_apply_pending'}
        // until the v2 migration is applied; only invalidate on a real insert.
        if (res.data.ok) await queryClient.invalidateQueries({ queryKey: ['notifications'] });
        return res.data;
      }
      // Transport-level failure — honest surface, never faked as persisted.
      return { ok: false, status: res.error.code };
    },
    [queryClient]
  );

  return useMemo(() => {
    const res = query.data;
    const ok = res && res.ok;
    return {
      notifications: ok ? res.data.notifications : [],
      unreadCount: ok ? res.data.unread_count : 0,
      openActionable: ok ? res.data.open_actionable ?? 0 : 0,
      delegatedCount: ok ? res.data.delegated_count ?? 0 : 0,
      tenantContextMissing: isTenantMissing(res),
      isLoading: query.isLoading,
      ack,
      act,
      delegate,
      archive,
      createClientGlobalNotification,
    };
  }, [query.data, query.isLoading, ack, act, delegate, archive, createClientGlobalNotification]);
}

export interface UseProjectsResult {
  projects: ProjectRow[];
  tenantContextMissing: boolean;
  isLoading: boolean;
}

export function useProjects(): UseProjectsResult {
  const authKey = useAuthStore((s) => s.identity?.id ?? null);
  const query = useQuery({
    queryKey: ['projects', authKey],
    staleTime: 30_000,
    retry: (n) => n < 1,
    queryFn: () => bffGet<ProjectsResponse>(PROJECTS_PATH),
  });
  return useMemo(() => {
    const res = query.data;
    return {
      projects: res && res.ok ? res.data.projects : [],
      tenantContextMissing: isTenantMissing(res),
      isLoading: query.isLoading,
    };
  }, [query.data, query.isLoading]);
}

export interface UseMeResult {
  me: MeResponse['identity'] | null;
  memberships: MeResponse['memberships'];
  isLoading: boolean;
}

export function useMe(): UseMeResult {
  const authKey = useAuthStore((s) => s.identity?.id ?? null);
  const query = useQuery({
    queryKey: ['me', authKey],
    staleTime: 30_000,
    retry: (n) => n < 1,
    queryFn: () => bffGet<MeResponse>(ME_PATH),
  });
  return useMemo(() => {
    const res = query.data;
    const ok = res && res.ok;
    return {
      me: ok ? res.data.identity : null,
      memberships: ok ? res.data.memberships : [],
      isLoading: query.isLoading,
    };
  }, [query.data, query.isLoading]);
}

export interface UseDocumentsResult {
  documents: DocumentRow[];
  tenantContextMissing: boolean;
  isLoading: boolean;
  refetch: () => void;
}

export function useDocuments(statusFilter?: string, projectId?: string): UseDocumentsResult {
  const authKey = useAuthStore((s) => s.identity?.id ?? null);
  const query = useQuery({
    queryKey: ['documents', statusFilter ?? 'all', projectId ?? 'all', authKey],
    staleTime: 20_000,
    retry: (n) => n < 1,
    queryFn: () =>
      bffGet<DocumentsResponse>(DOCUMENTS_PATH, {
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(projectId ? { project_id: projectId } : {}),
      }),
  });
  return useMemo(() => {
    const res = query.data;
    return {
      documents: res && res.ok ? res.data.documents : [],
      tenantContextMissing: isTenantMissing(res),
      isLoading: query.isLoading,
      refetch: () => void query.refetch(),
    };
  }, [query]);
}

export interface UseReportsResult {
  reports: ReportRow[];
  tenantContextMissing: boolean;
  isLoading: boolean;
  refetch: () => void;
}

export function useReports(statusFilter?: string, projectId?: string): UseReportsResult {
  const authKey = useAuthStore((s) => s.identity?.id ?? null);
  const query = useQuery({
    queryKey: ['reports', statusFilter ?? 'all', projectId ?? 'all', authKey],
    staleTime: 20_000,
    retry: (n) => n < 1,
    queryFn: () =>
      bffGet<ReportsResponse>(REPORTS_PATH, {
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(projectId ? { project_id: projectId } : {}),
      }),
  });
  return useMemo(() => {
    const res = query.data;
    return {
      reports: res && res.ok ? res.data.reports : [],
      tenantContextMissing: isTenantMissing(res),
      isLoading: query.isLoading,
      refetch: () => void query.refetch(),
    };
  }, [query]);
}

export interface UseOnboardingDraftResult {
  draft: Record<string, unknown> | null;
  version: number | null;
  tenantContextMissing: boolean;
  isLoading: boolean;
  /** Synchronous server-side upsert (user_persisted_state) — returns true on success. */
  saveDraft: (draft: Record<string, unknown>) => Promise<boolean>;
}

export function useOnboardingDraft(options: { enabled?: boolean } = {}): UseOnboardingDraftResult {
  const authKey = useAuthStore((s) => s.identity?.id ?? null);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['onboarding-draft', authKey],
    enabled: (options.enabled ?? true) && authKey !== null,
    staleTime: 30_000,
    retry: (n) => n < 1,
    queryFn: () => bffGet<OnboardingDraftResponse>(ONBOARDING_DRAFT_PATH),
  });

  const saveDraft = useCallback(
    async (draft: Record<string, unknown>): Promise<boolean> => {
      const res = await bffPatch<OnboardingDraftResponse>(ONBOARDING_DRAFT_PATH, draft);
      if (res.ok) {
        queryClient.setQueryData(['onboarding-draft', authKey], res);
        return true;
      }
      return false;
    },
    [queryClient, authKey]
  );

  return useMemo(() => {
    const res = query.data;
    const ok = res && res.ok;
    return {
      draft: ok ? res.data.draft : null,
      version: ok ? res.data.version : null,
      tenantContextMissing: isTenantMissing(res),
      isLoading: query.isLoading,
      saveDraft,
    };
  }, [query.data, query.isLoading, saveDraft]);
}

export interface UseConversationsResult {
  conversations: ConversationRow[];
  tenantContextMissing: boolean;
  isLoading: boolean;
  refetch: () => void;
}

export function useConversations(
  clientFrameId?: string,
  options: { enabled?: boolean; pollMs?: number } = {}
): UseConversationsResult {
  const authKey = useAuthStore((s) => s.identity?.id ?? null);
  const query = useQuery({
    queryKey: ['conversations', clientFrameId ?? 'all', authKey],
    enabled: (options.enabled ?? true) && authKey !== null,
    staleTime: 5_000,
    refetchInterval: options.pollMs ?? false,
    retry: (n) => n < 1,
    queryFn: () =>
      bffGet<ConversationsResponse>(CONVERSATIONS_PATH, {
        ...(clientFrameId ? { client_frame_id: clientFrameId } : {}),
      }),
  });
  return useMemo(() => {
    const res = query.data;
    return {
      conversations: res && res.ok ? res.data.conversations : [],
      tenantContextMissing: isTenantMissing(res),
      isLoading: query.isLoading,
      refetch: () => void query.refetch(),
    };
  }, [query]);
}

// --------------------------------------------------------------------------- //
// DevHub (ControlPlane) operator reads — gated to elevated roles in the views.
// Tenant-scoped via RLS; resolve to empty when the route is pending (outside-in).
// --------------------------------------------------------------------------- //
export interface ControlFrameRow {
  id: string;
  client_frame_id: string | null;
  intent: string;
  tool_route: string;
  risk_level: string;
  actor: string;
  status: string;
  requires_human_approval: boolean;
  created_at: string;
}
export interface ControlTraceRow {
  id: string;
  phase: string;
  actor: string;
  action: string;
  status: string;
  metadata: Record<string, unknown>;
  created_at: string;
  action_frame_client_id: string | null;
}
export interface ControlFinopsRow {
  id: string;
  actor: string;
  operation: string;
  model_id: string | null;
  route: string | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  total_tokens: number | null;
  cost_unit: number | null;
  created_at: string;
}

interface ControlFramesResponse { frames: ControlFrameRow[] }
interface ControlTracesResponse { traces: ControlTraceRow[] }
interface ControlFinopsResponse { usage: ControlFinopsRow[] }

/** Matrioska consolidated FinOps rollup (one row per subtree node × actor). */
export interface FinopsRollupRow {
  tenant_id: string;
  parent_tenant_id: string | null;
  slug: string;
  depth: number;
  actor: string;
  monthly_token_budget: number;
  used_tokens: number;
  balance: number;
  hard_stop: boolean;
}
interface ControlFinopsRollupResponse { rollup: FinopsRollupRow[] }

export function useControlActionFrames(limit = 50) {
  const authKey = useAuthStore((s) => s.identity?.id ?? null);
  const query = useQuery({
    queryKey: ['control-frames', limit, authKey],
    staleTime: 10_000,
    retry: (n) => n < 1,
    queryFn: () => bffGet<ControlFramesResponse>('/api/control/actionframes', { limit }),
  });
  return useMemo(() => {
    const res = query.data;
    return {
      frames: res && res.ok ? res.data.frames : [],
      isLoading: query.isLoading,
      refetch: () => void query.refetch(),
    };
  }, [query]);
}

export function useControlTraces(limit = 100) {
  const authKey = useAuthStore((s) => s.identity?.id ?? null);
  const query = useQuery({
    queryKey: ['control-traces', limit, authKey],
    staleTime: 10_000,
    retry: (n) => n < 1,
    queryFn: () => bffGet<ControlTracesResponse>('/api/control/traces', { limit }),
  });
  return useMemo(() => {
    const res = query.data;
    return {
      traces: res && res.ok ? res.data.traces : [],
      isLoading: query.isLoading,
      refetch: () => void query.refetch(),
    };
  }, [query]);
}

export function useControlFinops(limit = 100) {
  const authKey = useAuthStore((s) => s.identity?.id ?? null);
  const query = useQuery({
    queryKey: ['control-finops', limit, authKey],
    staleTime: 10_000,
    retry: (n) => n < 1,
    queryFn: () => bffGet<ControlFinopsResponse>('/api/control/finops', { limit }),
  });
  return useMemo(() => {
    const res = query.data;
    return {
      usage: res && res.ok ? res.data.usage : [],
      isLoading: query.isLoading,
      refetch: () => void query.refetch(),
    };
  }, [query]);
}

export function useControlFinopsRollup() {
  const authKey = useAuthStore((s) => s.identity?.id ?? null);
  const query = useQuery({
    queryKey: ['control-finops-rollup', authKey],
    staleTime: 10_000,
    retry: (n) => n < 1,
    queryFn: () => bffGet<ControlFinopsRollupResponse>('/api/control/finops', { view: 'rollup' }),
  });
  return useMemo(() => {
    const res = query.data;
    return {
      rollup: res && res.ok ? res.data.rollup : [],
      isLoading: query.isLoading,
    };
  }, [query]);
}

// --------------------------------------------------------------------------- //
// Per-user UX state (active project, last spoke, drafts) -> user_persisted_state
// via GET/PUT /api/user-state?key=<state_key>. Cross-device persistence; the
// LocalDeviceStore remains the offline cache. Resolves to null when the route is
// pending (outside-in).
// --------------------------------------------------------------------------- //
interface UserStateResponse<T> {
  key: string;
  state: T | null;
  version: number;
}

export interface UseUserStateResult<T> {
  state: T | null;
  isLoading: boolean;
  save: (state: T) => Promise<boolean>;
}

export function useUserState<T = Record<string, unknown>>(
  key: string,
  options: { enabled?: boolean } = {}
): UseUserStateResult<T> {
  const authKey = useAuthStore((s) => s.identity?.id ?? null);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['user-state', key, authKey],
    enabled: (options.enabled ?? true) && authKey !== null,
    staleTime: 30_000,
    retry: (n) => n < 1,
    queryFn: () => bffGet<UserStateResponse<T>>('/api/user-state', { key }),
  });

  const save = useCallback(
    async (state: T): Promise<boolean> => {
      const res = await bffPatch<UserStateResponse<T>>(`/api/user-state?key=${encodeURIComponent(key)}`, state as Record<string, unknown>);
      if (res.ok) {
        await queryClient.invalidateQueries({ queryKey: ['user-state', key] });
        return true;
      }
      return false;
    },
    [key, queryClient]
  );

  return useMemo(() => {
    const res = query.data;
    const ok = res && res.ok;
    return { state: ok ? res.data.state : null, isLoading: query.isLoading, save };
  }, [query.data, query.isLoading, save]);
}
